import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { WhatsappLogo, Briefcase, Wrench, FloppyDisk, CheckCircle, SpinnerGap } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfileSettings() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [whatsapp, setWhatsapp] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [specialty, setSpecialty] = useState('');

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/profile`, { withCredentials: true });
      setProfile(data);
      setWhatsapp(data.whatsapp_number || '');
      setBusinessName(data.business_name || '');
      setSpecialty(data.specialty || '');
    } catch (error) { console.error("Profile fetch error:", error.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, {
        whatsapp_number: whatsapp,
        business_name: businessName,
        specialty
      }, { withCredentials: true });
      toast.success('Profil mis à jour !');
    } catch (error) {
      console.error("Profile save error:", error.message);
      toast.error('Erreur de sauvegarde');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-8">
      <div className="animate-spin w-6 h-6 border-2 border-[#3F3F46] border-t-[#8B5CF6] rounded-full" />
    </div>
  );

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-5" data-testid="profile-settings">
      <h3 className="font-bold text-base text-[#F4F4F5] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Mon profil artisan</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2">
            <WhatsappLogo size={14} className="inline mr-1" weight="duotone" /> Numéro WhatsApp
          </label>
          <input value={whatsapp} onChange={e => setWhatsapp(e.target.value)} data-testid="profile-whatsapp"
            placeholder="Ex: +262 639 12 34 56"
            className="w-full h-12 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6] outline-none text-sm placeholder:text-[#71717A]" />
          <p className="text-xs text-[#71717A] mt-1">Sera inclus automatiquement dans tous vos posts générés</p>
        </div>
        <div>
          <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2">
            <Briefcase size={14} className="inline mr-1" weight="duotone" /> Nom de l'entreprise
          </label>
          <input value={businessName} onChange={e => setBusinessName(e.target.value)} data-testid="profile-business"
            placeholder="Ex: BTP Karibu Mayotte"
            className="w-full h-12 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6] outline-none text-sm placeholder:text-[#71717A]" />
        </div>
        <div>
          <label className="block text-xs font-bold tracking-[0.2em] uppercase text-[#71717A] mb-2">
            <Wrench size={14} className="inline mr-1" weight="duotone" /> Spécialité
          </label>
          <input value={specialty} onChange={e => setSpecialty(e.target.value)} data-testid="profile-specialty"
            placeholder="Ex: Toiture et étanchéité"
            className="w-full h-12 px-3 border border-[#27272A] rounded-lg bg-[#09090B] text-[#F4F4F5] focus:border-[#8B5CF6] focus:ring-1 focus:ring-[#8B5CF6] outline-none text-sm placeholder:text-[#71717A]" />
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-profile-btn"
          className="h-12 px-6 bg-[#8B5CF6] text-white font-bold rounded-lg hover:bg-[#7C3AED] transition-colors flex items-center gap-2 disabled:opacity-60">
          {saving ? <SpinnerGap size={18} className="animate-spin" /> : <FloppyDisk size={18} weight="duotone" />}
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
        {whatsapp && (
          <div className="flex items-center gap-2 p-3 bg-[#25D366]/10 border border-[#25D366]/20 rounded-lg">
            <CheckCircle size={16} weight="duotone" className="text-[#25D366]" />
            <span className="text-xs text-[#25D366]">Votre numéro {whatsapp} sera ajouté à tous vos posts</span>
          </div>
        )}
      </div>
    </div>
  );
}
